import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef } from 'react';
import { parsePhoneNumber } from 'libphonenumber-js';
import { Input } from '../ui/input';
// Phone Icon SVG component
const PhoneIcon = () => (_jsx("svg", { xmlns: "http://www.w3.org/2000/svg", width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", style: {
        position: 'absolute',
        left: '12px',
        top: '50%',
        transform: 'translateY(-50%)',
        pointerEvents: 'none',
        color: 'var(--muted-foreground, #71717a)'
    }, children: _jsx("path", { d: "M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" }) }));
export const PhoneInput = forwardRef(({ value, onChange, placeholder, showValidation, validationMessage, showIcon = true, autoComplete, disabled, className, id }, ref) => {
    // Format phone number for Azerbaijan: +994 XX XXX XX XX
    const formatPhoneNumber = (input) => {
        // Remove all non-numeric characters except '+'
        const cleaned = input.replace(/[^\d+]/g, '');
        // Handle empty or just '+' input
        if (!cleaned || cleaned === '+')
            return '+994 ';
        // Ensure it starts with +994
        let normalized = cleaned;
        if (!normalized.startsWith('+994')) {
            if (normalized.startsWith('994')) {
                normalized = `+${normalized}`;
            }
            else if (normalized.startsWith('+')) {
                // Keep international numbers as is
                return normalized;
            }
            else {
                // Prepend +994 to local numbers
                normalized = `+994${normalized}`;
            }
        }
        // Extract digits after +994
        const countryCode = '+994';
        const digits = normalized.slice(4);
        // Apply formatting: +994 XX XXX XX XX
        if (digits.length === 0)
            return `${countryCode} `;
        if (digits.length <= 2)
            return `${countryCode} ${digits}`;
        if (digits.length <= 5)
            return `${countryCode} ${digits.slice(0, 2)} ${digits.slice(2)}`;
        if (digits.length <= 7)
            return `${countryCode} ${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5)}`;
        // Full format: +994 XX XXX XX XX (max 9 digits)
        return `${countryCode} ${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5, 7)} ${digits.slice(7, 9)}`;
    };
    // Validate phone number using libphonenumber-js
    const isValid = (() => {
        if (!value || value.length < 10)
            return false;
        try {
            const phoneNumber = parsePhoneNumber(value, 'AZ');
            return phoneNumber?.isValid() || false;
        }
        catch {
            // If parsing fails, do basic validation
            const cleaned = value.replace(/[^\d]/g, '');
            return cleaned.length >= 12 && cleaned.startsWith('994');
        }
    })();
    const handleChange = (e) => {
        const formatted = formatPhoneNumber(e.target.value);
        onChange(formatted);
    };
    const handleFocus = (_e) => {
        // Auto-add +994 on focus if empty
        if (!value || value.trim() === '') {
            onChange('+994 ');
        }
    };
    return (_jsxs("div", { className: className, children: [_jsxs("div", { style: { position: 'relative' }, children: [showIcon && _jsx(PhoneIcon, {}), _jsx(Input, { ref: ref, id: id, type: "tel", value: value, onChange: handleChange, onFocus: handleFocus, placeholder: placeholder || '+994 XX XXX XX XX', autoComplete: autoComplete || 'tel', disabled: disabled, style: { paddingLeft: showIcon ? '44px' : undefined }, className: showValidation && value && !isValid ? 'border-destructive' : '' })] }), showValidation && value && !isValid && (_jsx("p", { className: "text-sm text-destructive mt-1", children: validationMessage || 'Введите корректный номер телефона' }))] }));
});
PhoneInput.displayName = 'PhoneInput';
